import React, { useEffect, useState } from 'react';
import TailwindLayout from '~/libs/ui/atoms/TailwindLayout/TailwindLayout';
import { SpinningLoadingIcon } from '~/libs/ui/atoms/SpinningLoadingIcon';
import { ErpRuleHelpInformationResponse } from 'com.cogvio.pricemonitor.api';
import { getErpHelp } from '~/libs/infrastructure/api/erp';
import CountryIcon from '~/UI/CountryIcon';

interface ErpRuleHelpWithHospital extends ErpRuleHelpInformationResponse {
	hospital?: ErpRuleHelpInformationResponse;
}

export function IRPHelpPage() {
	const [error, setError] = useState(false);
	const [loading, setLoading] = useState(true);
	const [help, setHelp] = useState<ErpRuleHelpWithHospital[]>([]);

	useEffect(() => {
		getErpHelp()
			.then((data) => {
				const hospital = data.items.filter((x) => x.productType.toLowerCase() === 'hospital');
				const retail = data.items.filter((x) => x.productType.toLowerCase() === 'retail');

				const result = retail.map((item) => {
					const assigned = hospital.find((x) => x.country === item.country);
					return Object.assign(item, { hospital: assigned });
				});

				setHelp(result);
			})
			.catch(() => {
				setError(true);
			})
			.finally(() => {
				setLoading(false);
			});
	}, []);

	return (
		<TailwindLayout>
			<div className='md:p-10 p-4'>
				<div className='w-full max-w-5xl mx-auto'>
					<div className='mb-6 bg-cPurple-50 bg-opacity-10 border border-cPurple-500 rounded-md p-4' role='alert'>
						<div className='flex'>
							<div className='flex-shrink-0'>
								<svg
									className='h-4 w-4 text-cPurple-500 mt-1'
									fill='currentColor'
									height='16'
									viewBox='0 0 16 16'
									width='16'
									xmlns='http://www.w3.org/2000/svg'
								>
									<path d='M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z'></path>
								</svg>
							</div>
							<div className='ml-4'>
								<h3 className='text-lg font-display text-cPurple-500 font-semibold'>Disclaimer</h3>
								<div className='mt-2 text-md leading-6 text-gray-600'>
									Cogvio IRP model calculates prices of original, prescription,{' '}
									<strong>hospital and non-hospital</strong> medicines in 30 countries of Europe. IRP effect is
									calculated only in countries where IRP method is used. In case the price is set during negotiations in
									some countries, it remains fixed in the model. The price revision is set on a specific date or is
									estimated from a minimum frequency determined by the relevant authority.
								</div>
							</div>
						</div>
					</div>
					{loading && (
						<div className='w-full py-5 flex justify-center items-center'>
							<div className='h-12'>
								<SpinningLoadingIcon />
							</div>
						</div>
					)}
					{!loading && !error && (
						<div className='px-10 pt-2 pb-10 bg-white'>
							{help.map((data) => (
								<div key={data.country}>
									<div className=' pt-4 pb-5 border-t-2 border-gray-200'>
										<div className='w-full mb-1'>
											<span className='flex items-center font-display font-semibold text-xl text-cPurple-500'>
												<CountryIcon
													className='mr-2 w-5 h-5'
													code={data.alpha2 || data.country.substring(0, 2).toUpperCase()}
													fullSizeFlags
												/>
												{data.country}
											</span>
										</div>

										<div className='pl-7 flex flex-wrap md:flex-nowrap md:flex-row flex-col'>
											<div className='md:w-64 w-full md:mb-0 mb-4 flex-shrink-0'>
												<p className='mb-1 text-cPurple-700 text-base'>
													<strong>Retail</strong>
												</p>
												<h2 className='block font-display font-semibold text-label text-gray-900'>
													Price setting method:
												</h2>
												<p className='leading-relaxed text-md text-gray-600'>{data.priceSettingMethod}</p>
											</div>
											<div className='flex-grow md:pl-6'>
												<p className='mb-1 text-base'>&nbsp;</p>
												<div className='mb-4'>
													<h2 className='font-display font-semibold text-label text-gray-900'>Basket of countries:</h2>
													<p className='leading-relaxed text-md text-gray-600'>{data.basketOfCountries}</p>
												</div>

												<h2 className='font-display font-semibold text-label text-gray-900'>IRP rule:</h2>
												<p className='leading-relaxed text-md text-gray-600 whitespace-pre-line'>{data.irpRule}</p>
											</div>
										</div>
									</div>

									{data.hospital && (
										<div className=' pb-5'>
											<div className='pl-7 pb-4'>
												<hr className='border-t border-gray-200' />
											</div>
											<div className='pl-7 flex flex-wrap md:flex-nowrap md:flex-row flex-col'>
												<div className='md:w-64 w-full md:mb-0 mb-4 flex-shrink-0'>
													<p className='mb-1 text-cPurple-700 text-base'>
														<strong>Hospital</strong>
													</p>

													<h2 className='block font-display font-semibold text-label text-gray-900'>
														Price setting method:
													</h2>
													<p className='leading-relaxed text-md text-gray-600'>{data.hospital.priceSettingMethod}</p>
												</div>
												<div className='flex-grow md:pl-6'>
													<p className='mb-1 text-base'>&nbsp;</p>
													<div className='mb-4'>
														<h2 className='font-display font-semibold text-label text-gray-900'>
															Basket of countries:
														</h2>
														<p className='leading-relaxed text-md text-gray-600'>{data.hospital.basketOfCountries}</p>
													</div>

													<h2 className='font-display font-semibold text-label text-gray-900 '>IRP rule:</h2>
													<p className='leading-relaxed text-md text-gray-600 whitespace-pre-line'>
														{data.hospital.irpRule}
													</p>
												</div>
											</div>
										</div>
									)}
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		</TailwindLayout>
	);
}
